import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const grimaudEn: ChauffeurPage = {
  slug: "grimaud",
  title: "VTC & private chauffeur in Grimaud",
  subtitle:
    "Private chauffeur in Grimaud: the medieval hilltop village above the Gulf of Saint-Tropez, Nice airport transfers and hourly hire.",
  keywords: [
    "vtc grimaud",
    "private chauffeur grimaud",
    "grimaud airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Grimaud",
    subtitle:
      "The medieval hilltop village overlooking the gulf: Nice airport transfer, hourly hire and long distance, 7 days a week.",
    keywords: [
      "VTC Grimaud",
      "Grimaud village chauffeur",
      "Nice airport transfer",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Grimaud (Gulf of Saint-Tropez)",
    badges: [
      "Medieval village & Port Grimaud",
      "Nice airport transfer",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Grimaud",
    items: [
      {
        title: "Hilltop village handled",
        description:
          "Steep lanes, the castle and car parks set back from the centre: the chauffeur drops you as close as possible and returns on request.",
      },
      {
        title: "Across the whole gulf",
        description:
          "Between the upper village and Port Grimaud below, a smooth link across the gulf with no parking constraint.",
      },
      {
        title: "Long-distance transfer",
        description:
          "From Nice airport, around 1h20 to 1h30 of road handled in high-end comfort.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Grimaud",
    services: [
      {
        title: "Nice airport transfer",
        text: "Grimaud ⇄ Nice Côte d’Azur airport with flight tracking; a longer run than from Cannes, planned from pick-up.",
      },
      {
        title: "Saint-Raphaël TGV station",
        text: "Pick-up at Saint-Raphaël station, around 30 to 40 minutes away, as an alternative rail transfer point.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day for the village, Port Grimaud, the beaches and the gulf’s villas.",
      },
      {
        title: "Saint-Tropez link",
        text: "Grimaud village ⇄ Saint-Tropez, the port and nights out, with safe late-night returns.",
      },
      {
        title: "Long distance",
        text: "Trips of any distance from Grimaud: Cannes, Nice, Monaco and beyond, with a dedicated chauffeur.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Grimaud village",
        description:
          "Medieval hilltop town, cobbled lanes and the rue des Templiers.",
      },
      {
        name: "Grimaud castle",
        description: "Ruins of the feudal castle overlooking the gulf.",
      },
      {
        name: "Saint-Roch windmill",
        description: "Restored windmill above the village.",
      },
      {
        name: "Port Grimaud",
        description: "Lakeside town below, the ‘Provençal Venice’.",
      },
      {
        name: "Saint-Raphaël station",
        description: "TGV station around 30-40 min away, a transfer point.",
      },
      {
        name: "Nice airport",
        description: "Nice Côte d’Azur airport, around 1h20-1h30 away.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur for Grimaud and its gulf",
    subheading:
      "From the medieval hilltop village to Port Grimaud, a premium service across the Gulf of Saint-Tropez.",
    paragraphs: [
      "Grimaud commands the Gulf of Saint-Tropez from its spur: the medieval village, the castle ruins and the windmills watch over Port Grimaud, the lakeside town below. That gradient, and parking set well back from the pedestrian core, make a chauffeur especially useful — dropping you close in and returning at a single call.",
      "The village has no airport of its own: most arrivals come through Nice Côte d’Azur, around 1h20 to 1h30 away depending on summer traffic. We set the departure time to limit waiting, and offer Saint-Raphaël’s TGV station, some thirty minutes away, as an alternative transfer point.",
      "On the ground, one chauffeur links the upper village, Port Grimaud, the beaches and Saint-Tropez, with the flexibility villa guests expect in high season. Fixed fares agreed in advance or hourly hire: an immediate quote for every journey.",
    ],
    bullets: [
      "Drop-off close to the village",
      "Nice airport & Saint-Raphaël station transfers",
      "Hourly hire across the gulf",
    ],
  },
  process: {
    heading: "Book a chauffeur in Grimaud",
    steps: [
      "Tell us your route (village, Port Grimaud, airport, station) and times.",
      "We recommend the right vehicle and fare.",
      "The chauffeur tracks your flight or train and adjusts departure.",
      "Direct transfer or hourly hire across the gulf during your stay.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Grimaud"),
    {
      q: "How long is the drive to Nice airport from Grimaud?",
      a: "Around 1h20 to 1h30 outside peak times, more on summer weekends and traffic peaks in the gulf. We adjust the departure and track your flight.",
    },
    {
      q: "Do you serve Saint-Raphaël TGV station?",
      a: "Yes. Saint-Raphaël station is around 30 to 40 minutes from Grimaud; it is a handy alternative transfer point for train arrivals and departures.",
    },
    {
      q: "Do you handle the link with Saint-Tropez?",
      a: "Yes, Grimaud is very close to Saint-Tropez; we run round trips to the port, restaurants and nights out, with safe late-night returns.",
    },
    {
      q: "How far ahead should I book?",
      a: "In high summer season, book as early as possible: demand is strong across the Gulf of Saint-Tropez. We also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele and villa stays.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes sedan, premium van (V-Class), SUV and minibus depending on group size and luggage, suited to the long run from Nice or Cannes.",
    },
    {
      q: "Can you drive into the hilltop village?",
      a: "The historic centre is largely pedestrian and steep. The chauffeur drops you as close as possible to the village entrance and collects you at the same spot, with no hunt for parking.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Airport transfers in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Car rental in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Grimaud",
    text: "Nice airport transfer, Saint-Raphaël station or a night in Saint-Tropez: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Grimaud — private chauffeur & transfers",
    description:
      "Private car service in Grimaud: Nice airport transfer, Saint-Raphaël TGV station, hourly hire and the Saint-Tropez link. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/grimaud",
  },
};

export default grimaudEn;
